import { useEffect, useState } from 'react';
import { Button } from '@/components';
import { formatCost, formatDateTime, formatTokens } from '@/lib/format';
import type { UsageResponse } from '@/lib/api';
import type { BudgetState, ModelOption, WorkspaceSettings, WorkspaceUser } from '@shared/types';
import { UsersPanel } from './UsersPanel';
import './SettingsScreen.scss';

export interface SettingsScreenProps {
  settings: WorkspaceSettings;
  budget: BudgetState;
  models: ModelOption[];
  usage: UsageResponse;
  canEdit: boolean;
  busy: boolean;
  onSave: (patch: {
    monthlyTokenLimit?: number;
    alertThresholdPct?: number;
    defaultModel?: string;
  }) => void;
  /** Empty for a member: the endpoint behind it is administrators only. */
  users: WorkspaceUser[];
  currentUserId: string;
  onInvite: (email: string, role: 'member' | 'admin') => Promise<boolean>;
  onChangeRole: (userId: string, role: 'member' | 'admin') => void;
  onRemove: (userId: string) => void;
}

const STATUS_COPY: Record<BudgetState['status'], string> = {
  ok: 'Within budget',
  warning: 'Approaching limit',
  blocked: 'Limit reached',
};

type Tab = 'workspace' | 'users';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'workspace', label: 'Workspace' },
  { key: 'users', label: 'Users' },
];

const LEDE: Record<Tab, string> = {
  workspace: 'Set a monthly token budget for your workspace and track what every request costs.',
  users: 'Invite the people who should reach this workspace, and see who already can.',
};

export const SettingsScreen: React.FC<SettingsScreenProps> = ({
  settings,
  budget,
  models,
  usage,
  canEdit,
  busy,
  onSave,
  users,
  currentUserId,
  onInvite,
  onChangeRole,
  onRemove,
}) => {
  const [limitInput, setLimitInput] = useState(String(settings.monthlyTokenLimit));
  const [alertInput, setAlertInput] = useState(String(settings.alertThresholdPct));
  const [tab, setTab] = useState<Tab>('workspace');

  // Re-sync when a save round-trips or another admin changes the budget.
  useEffect(() => {
    setLimitInput(String(settings.monthlyTokenLimit));
    setAlertInput(String(settings.alertThresholdPct));
  }, [settings.monthlyTokenLimit, settings.alertThresholdPct]);

  const unlimited = settings.monthlyTokenLimit <= 0;
  const activeModel = models.find((m) => m.model === settings.defaultModel);
  // A member never gets the tab, so they can never be left on it.
  const activeTab: Tab = canEdit ? tab : 'workspace';

  function saveLimits() {
    const limit = Number.parseInt(limitInput.replace(/[^\d]/g, ''), 10);
    const alert = Number.parseInt(alertInput.replace(/[^\d]/g, ''), 10);
    onSave({
      monthlyTokenLimit: Number.isFinite(limit) ? limit : 0,
      alertThresholdPct: Number.isFinite(alert) ? Math.min(100, Math.max(0, alert)) : 80,
    });
  }

  return (
    <div className="screen screen--narrow">
      <div className="screen__eyebrow">Workspace</div>
      <h1 className="screen__title">Settings</h1>
      <p className="screen__lede">{LEDE[activeTab]}</p>

      {/* Only an administrator can reach the users API, so a member is not
          offered a tab that would do nothing but 403. */}
      {canEdit && (
        <div className="settings__tabs" role="tablist">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activeTab === item.key}
              className={`settings__tab${activeTab === item.key ? ' settings__tab--active' : ''}`}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'users' ? (
        <UsersPanel
          users={users}
          currentUserId={currentUserId}
          onInvite={onInvite}
          onChangeRole={onChangeRole}
          onRemove={onRemove}
        />
      ) : (
        <>
          <div className="budget">
            <div className="budget__head">
              <div>
                <div className="budget__label">Token budget</div>
                <div className="budget__used">{formatTokens(budget.used)} tokens used</div>
                <div className="budget__meta">
                  {unlimited
                    ? 'No limit set'
                    : `of ${formatTokens(budget.limit)} · ${formatTokens(budget.remaining)} remaining`}
                </div>
              </div>
              <div className="budget__gauge">
                <span className={`budget__chip budget__chip--${budget.status}`}>
                  {STATUS_COPY[budget.status]}
                </span>
                <div className="budget__pct">{unlimited ? '--' : `${budget.pct.toFixed(0)}%`}</div>
              </div>
            </div>

            <div className="budget__track">
              <div
                className={`budget__fill budget__fill--${budget.status}`}
                style={{ width: unlimited ? '0%' : `${budget.pct}%` }}
              />
            </div>

            <div className="budget__footer">
              <span>Spend to date {formatCost(budget.spendUsd)}</span>
              <span>
                {unlimited ? 'Unlimited' : `Alerts at ${settings.alertThresholdPct}% of limit`}
              </span>
            </div>
          </div>

          <div className="settings__grid">
            <div className="settings__card">
              <div className="field-label">Monthly token limit</div>
              <p className="settings__hint">
                Requests are blocked once the workspace hits this cap. Use 0 for no limit.
              </p>
              <input
                className="text-input text-input--mono"
                value={limitInput}
                inputMode="numeric"
                disabled={!canEdit}
                onChange={(e) => setLimitInput(e.target.value)}
              />
              <div className="settings__actions">
                <Button size="sm" loading={busy} disabled={!canEdit} onClick={saveLimits}>
                  Save limits
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!canEdit}
                  onClick={() => onSave({ monthlyTokenLimit: 0 })}
                >
                  Remove limit
                </Button>
              </div>
            </div>

            <div className="settings__card">
              <div className="field-label">Alert threshold</div>
              <p className="settings__hint">
                Get a heads-up when usage passes this share of the limit.
              </p>
              <div className="settings__alert-row">
                <input
                  className="text-input text-input--mono settings__alert-input"
                  value={alertInput}
                  inputMode="numeric"
                  disabled={!canEdit}
                  onChange={(e) => setAlertInput(e.target.value)}
                />
                <span className="settings__alert-suffix">% of limit</span>
              </div>
            </div>
          </div>

          <div className="settings__card settings__card--wide">
            <div className="field-label">Default model</div>
            <p className="settings__hint">
              Agents use this unless they override it. Rates below drive the cost figures.
            </p>
            <select
              className="select-input"
              value={settings.defaultModel}
              disabled={!canEdit}
              onChange={(e) => onSave({ defaultModel: e.target.value })}
            >
              {models.map((m) => (
                <option key={m.model} value={m.model}>
                  {m.displayName} ({m.provider})
                </option>
              ))}
            </select>

            {activeModel && (
              <div className="settings__pricing">
                <div>
                  <div className="settings__price">${activeModel.inputPerMTok.toFixed(2)}</div>
                  <div className="settings__price-label">per 1M input tokens</div>
                </div>
                <div>
                  <div className="settings__price">${activeModel.outputPerMTok.toFixed(2)}</div>
                  <div className="settings__price-label">per 1M output tokens</div>
                </div>
              </div>
            )}
          </div>

          <div className="settings__card settings__card--wide">
            <div className="settings__table-head">
              <div className="field-label">Usage by request</div>
              <div className="settings__table-total">
                {usage.totals.requestCount} request{usage.totals.requestCount === 1 ? '' : 's'} ·{' '}
                {formatCost(usage.totals.costUsd)}
              </div>
            </div>

            <div className="usage-table">
              <div className="usage-table__head">
                <span>Agent</span>
                <span>When</span>
                <span>Input</span>
                <span>Output</span>
                <span>Total</span>
                <span className="usage-table__right">Cost</span>
              </div>
              {usage.requests.map((row) => (
                <div key={row.id} className="usage-table__row">
                  <span className="usage-table__agent">
                    {row.agentName}
                    {row.status !== 'ok' && (
                      <span className={`usage-table__flag usage-table__flag--${row.status}`}>
                        {row.status}
                      </span>
                    )}
                  </span>
                  <span className="usage-table__when">{formatDateTime(row.createdAt)}</span>
                  <span className="usage-table__num">{formatTokens(row.inputTokens)}</span>
                  <span className="usage-table__num">{formatTokens(row.outputTokens)}</span>
                  <span className="usage-table__num usage-table__num--strong">
                    {formatTokens(row.totalTokens)}
                  </span>
                  <span className="usage-table__num usage-table__num--strong usage-table__right">
                    {formatCost(row.costUsd)}
                  </span>
                </div>
              ))}
            </div>

            {usage.requests.length === 0 && (
              <div className="empty-note">
                No requests yet — chat with an agent and each request lands here with its token
                count and cost.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsScreen;
